import { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Space } from 'antd';
import { FormikProps, setNestedObjectValues, useFormik } from 'formik';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import useIcs2ArrivalNotification, {
    arrivalErrorMessage,
    arrivalFieldErrors,
    arrivalValidationError,
} from 'hooks/useIcs2ArrivalNotification';
import { ErrorResponse } from 'core/http/response';
import SubmissionErrorsDrawerButton from 'views/declarations/common/submissionErrors/SubmissionErrorsDrawerButton';
import { DeclarationStatuses } from 'store/declarations/enums/common/declaration-status';
import {
    ICS2ArrivalData,
    ICS2ArrivalNotificationMessage,
    ICS2ArrivalView,
} from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';
import validate, { FormModel, transformErrorsForFormik } from 'views/declarations/uk/export/validations/validations';
import ics2ArrivalNotificationValidation from 'views/declarations/eu/ics2/validation/Ics2ArrivalNotificationValidation';
import { DeclarationContextProvider } from 'views/declarations/common/DeclarationContext';
import {
    BtMasterGoodsSelected,
    FormTabsWrapper,
    StyledHeader,
    StyledLayout,
    StyledTabs,
} from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import DeclarationStatusTag from 'components/ui/composed/declarations/declaration-status/DeclarationStatusTag';
import Button from 'components/new-ui/base/button/Button';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import Notification from 'components/ui/base/notification/Notification';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import useArrivalAutoRefresh from 'hooks/useArrivalAutoRefresh';
import useBreadcrumb from 'hooks/useBreadcrumb';
import ArrivalNotificationsDrawerButton from './ArrivalNotificationsDrawerButton';
import ArrivalManifestLink from './ArrivalManifestLink';
import { arrivalBreadcrumbs } from './arrivalBreadcrumbs';

export const ArrivalNotificationTabKeys = {
    FORM: 'form',
    HISTORY: 'history',
} as const;

export type ArrivalNotificationTabKey = typeof ArrivalNotificationTabKeys[keyof typeof ArrivalNotificationTabKeys];

export interface ArrivalNotificationOutletContext {
    formik: FormikProps<ICS2ArrivalData>;
    arrival?: ICS2ArrivalView;
    messages: ICS2ArrivalNotificationMessage[];
    isEditable: boolean;
}

const ArrivalNotificationPage = () => {
    const { arrivalNotificationId } = useParams<{ arrivalNotificationId: string }>();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { arrival, messages, isLoading, isSaving, isSubmitting, isEditable, load, loadMessages, save, submit } =
        useIcs2ArrivalNotification();

    useArrivalAutoRefresh(arrival, load, loadMessages);

    const { setBreadcrumbRoutes } = useBreadcrumb();

    useEffect(() => {
        setBreadcrumbRoutes(arrivalBreadcrumbs(arrival));
    }, [arrival, setBreadcrumbRoutes]);

    const [initialValues, setInitialValues] = useState<ICS2ArrivalData>({});

    useEffect(() => {
        if (!arrivalNotificationId) return;

        load(arrivalNotificationId)
            .then((view) => setInitialValues(view.data ?? {}))
            .catch((error) =>
                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Could not load the arrival notification.',
                    description: arrivalErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
                })
            );

        loadMessages(arrivalNotificationId).catch(() => {
            /* The history is supporting detail; failing to load it must not block the form. */
        });
    }, [arrivalNotificationId, load, loadMessages]);

    const formik = useFormik<ICS2ArrivalData>({
        initialValues,
        enableReinitialize: true,
        validateOnChange: false,
        validate: async (values) =>
            transformErrorsForFormik(await validate(new FormModel(values), ics2ArrivalNotificationValidation(values))),
        onSubmit: () => {},
    });

    const onSave = useCallback(async () => {
        try {
            const view = await save(formik.values);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Arrival notification saved.',
                description: 'Successfully saved draft arrival notification.',
            });

            return view;
        } catch (error) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Saving the arrival notification failed.',
                description: arrivalErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
            return undefined;
        }
    }, [formik.values, save]);

    /**
     * The last submission the service rejected on validation. It is cleared when submitting again,
     * which unmounts the errors button, so a further rejection mounts it anew and reopens the drawer.
     */
    const [submissionError, setSubmissionError] = useState<ErrorResponse | undefined>();

    const onSubmitToCustoms = useCallback(async () => {
        setSubmissionError(undefined);

        const errors = await formik.validateForm();
        if (Object.keys(errors).length) {
            formik.setTouched(setNestedObjectValues(errors, true), false);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'The arrival notification is not complete.',
                description: 'Fix the highlighted fields before submitting.',
            });
            return;
        }

        const saved = await onSave();
        if (!saved) return;

        try {
            const result = await submit(saved);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Arrival notification queued.',
                description: `LRN ${result.lrn ?? '-'}. Waiting for customs to register it.`,
            });
        } catch (error) {
            const fieldErrors = arrivalFieldErrors(error);
            if (fieldErrors.length) {
                const errors = transformErrorsForFormik(
                    Object.fromEntries(fieldErrors.map(({ field, message }) => [field, message]))
                );
                formik.setErrors(errors);
                formik.setTouched(setNestedObjectValues(errors, true), false);
            }

            const validationError = arrivalValidationError(error);
            setSubmissionError(validationError);

            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Submitting the arrival notification failed.',
                description: validationError
                    ? 'Fix the errors listed in the submission errors drawer.'
                    : arrivalErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        }
    }, [formik, onSave, submit]);

    const baseUrl = `/arrival-notifications/${arrivalNotificationId}`;

    const activeTabKey: ArrivalNotificationTabKey = pathname.endsWith(`/${ArrivalNotificationTabKeys.HISTORY}`)
        ? ArrivalNotificationTabKeys.HISTORY
        : ArrivalNotificationTabKeys.FORM;

    const outletContext: ArrivalNotificationOutletContext = useMemo(
        () => ({ formik, arrival, messages, isEditable }),
        [formik, arrival, messages, isEditable]
    );

    return (
        <DeclarationContextProvider>
            <StyledLayout>
                <StyledHeader>
                    <PageHeader
                        showBackButton
                        icon={null}
                        onBackButtonClick={() => navigate(-1)}
                        title="Arrival Notification ENS"
                        additionalContent={
                            <Space size={16} align="center" style={{ marginLeft: 16 }}>
                                {arrival?.status && (
                                    <DeclarationStatusTag
                                        status={arrival.status as unknown as DeclarationStatuses}
                                        dataTestId="arrival-notification-status"
                                    />
                                )}
                            </Space>
                        }
                        sidebar={
                            <Space size={16}>
                                <Button onClick={onSave} loading={isSaving} disabled={!isEditable || isLoading}>
                                    Save as Draft
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={onSubmitToCustoms}
                                    loading={isSubmitting}
                                    disabled={!isEditable || isLoading}
                                >
                                    Submit
                                </Button>
                            </Space>
                        }
                    />

                    <StyledTabs
                        activeKey={activeTabKey}
                        onChange={(key) =>
                            navigate(key === ArrivalNotificationTabKeys.FORM ? baseUrl : `${baseUrl}/${key}`)
                        }
                        items={[
                            { key: ArrivalNotificationTabKeys.FORM, label: 'Arrival Notification' },
                            { key: ArrivalNotificationTabKeys.HISTORY, label: 'History' },
                        ]}
                    />
                </StyledHeader>

                {activeTabKey === ArrivalNotificationTabKeys.FORM && (
                    <StyledHeader>
                        <Row justify="start" align="middle" style={{ gap: '2rem', marginBottom: '0.8rem' }}>
                            <FormTabsWrapper>
                                <BtMasterGoodsSelected left>Master Information</BtMasterGoodsSelected>
                            </FormTabsWrapper>

                            <ArrivalManifestLink
                                reference={arrival?.data?.reference}
                                manifestId={arrival?.sourceManifestId}
                            />

                            <div style={{ marginLeft: 'auto' }}>
                                {submissionError ? (
                                    <SubmissionErrorsDrawerButton declarationError={submissionError} />
                                ) : (
                                    <ArrivalNotificationsDrawerButton arrival={arrival} messages={messages} />
                                )}
                            </div>
                        </Row>
                    </StyledHeader>
                )}

                <Outlet context={outletContext} />
            </StyledLayout>
        </DeclarationContextProvider>
    );
};

export default ArrivalNotificationPage;
